import React from 'react';
import { Helmet } from 'react-helmet';

import useSiteMetadata from '../hooks/useSiteMetadata';

const SEO = ({ children, title, subTitle, meta, location, ...rest }) => {
  const {
    description: metaDescription,
    keywords: metaKeywords,
    noindex,
    nofollow,
    title: metaTitle,
    image,
  } = meta || {};
  const {
    keywords,
    title: siteTitle,
    titleTemplate,
    siteUrl,
    twitter,
  } = useSiteMetadata();
  const { company, coverImage, friendlyStartDate, type } = rest;
  const { pathname } = location;
  const pageTitle = metaTitle || title;
  const pageDescription = metaDescription || subTitle;
  const pageKeywords = metaKeywords || keywords;

  const buildURL = (url, obj) => {
    const query = Object.entries(obj)
      .map((pair) => pair.map(encodeURIComponent).join('='))
      .join('&');

    return `${url}?${query}`;
  };

  const isDocsPage = pathname.includes('/docs');

  const ogType = () => (type ? type : 'PAGE');

  const ogImageSrc = () => {
    if (!type || isDocsPage || !coverImage)
      return `${siteUrl}/default-product-image.png`;

    return coverImage.url;
  };

  const ogImageUrl = buildURL(`${siteUrl}/api/og-image`, {
    image: ogImageSrc(),
    ...(friendlyStartDate && { date: friendlyStartDate }),
    title: company ? company.name : title,
    type: isDocsPage ? 'DOCS' : ogType(),
  });

  const ogImage = image?.url || ogImageUrl;
  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      defaultTitle={siteTitle}
      titleTemplate={titleTemplate}
    >
      <title>{pageTitle}</title>
      {pathname === '/' && <link rel="canonical" href={`${siteUrl}/`} />}
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta property="image" content={ogImage} />

      <meta property="og:url" content={`${siteUrl}${pathname}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:type" content={(coverImage && coverImage.mimeType) ? coverImage.mimeType : "image/png"} />
      <meta name="og:type" content="website" />

      <meta name="twitter:site" content={`@${twitter}`} />
      <meta name="twitter:title" content={pageTitle || siteTitle} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "url": "${siteUrl}",
          "logo": "${siteUrl}/logo.svg"
        }`}
      </script>
      {!!noindex && <meta name="robots" content="noindex" />}
      {!!noindex && !!nofollow && <meta name="robots" content="noindex, nofollow" />}

      {children}
    </Helmet>
  );
};

export default SEO;
